/**
 * BillingRowEditor — per-row inline edit panel for the firm-wide Billing-details table
 * (OPEN_ITEMS #8, Inc 4a). Renders inside the account row's expanded detail cell.
 *
 * §3g Pattern B (per-row commit): this is NOT an Edit-mode form-transaction — the panel's
 * own "Save changes" button IS the commit gesture. All three billing write endpoints are
 * DIRECT Admin writes (audited, no maker-checker), so Save fires them immediately and
 * closes; there is no staged batch across rows. One row is edited at a time.
 *
 * Edits both AXES the firm-wide surface owns (identity moves fully to Admin — the
 * Agreement side becomes a read-only mirror in Inc 5):
 *   - assignment: fee schedule (available-to-this-agreement) + frequency + VAT
 *   - identity:   billing name / TIN / address, or a snapshot-from-related-party
 *
 * On Save it invalidates the firm-wide list AND the per-agreement mirror queries
 * (C-204/C-221) so both surfaces reflect the change on the next fetch. Server-derived
 * drift/completeness/fee are never recomputed here (C-214).
 */
import { useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Group, NumberInput, Select, Stack, Text, TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconUser, IconX } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { accountBillingApi, type FirmWideBillingRow } from '../api/accountBilling';
import { agreementPartiesApi } from '../api/agreementParties';
import { bpsToPct, feeSchedulesApi, pctToBps } from '../api/feeSchedules';
import { personsApi } from '../api/persons';
import { EntityPicker } from './EntityPicker';
import type { Person, UUID } from '../api/types';

const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semiannual' },
];

function composeAddress(...parts: (string | null | undefined)[]): string {
  return parts.map((p) => (p ?? '').trim()).filter(Boolean).join(' | ');
}
// PREVIEW-ONLY mapping of a party's billing fields (mirrors the backend so the user sees
// what a snapshot will copy; the Save is server-authoritative). #70 full delimited address.
function previewFromPerson(p: Person): { name: string; tin: string; address: string } {
  const legal = p.kind === 'LEGAL';
  return {
    name: p.display_name ?? '',
    tin: (legal ? p.tin : p.tin_1) ?? '',
    address: legal
      ? composeAddress(p.registered_office_address, p.registered_office_postal_code,
          p.registered_office_city, p.registered_office_state, p.registered_office_country)
      : composeAddress(p.fiscal_address, p.fiscal_address_postal_code,
          p.fiscal_address_city, p.fiscal_address_state, p.fiscal_address_country),
  };
}

export function BillingRowEditor({ row, onClose }: { row: FirmWideBillingRow; onClose: () => void }) {
  const qc = useQueryClient();
  const agreementId = row.agreement_id;
  const accountId = row.account_id;

  // Schedules assignable to THIS agreement (server-filtered — availability is not
  // re-derived client-side). Read-open, so this loads for the admin editing here.
  const availableQ = useQuery({
    queryKey: ['fee-schedules', 'available', agreementId],
    queryFn: () => feeSchedulesApi.availableForAgreement(agreementId),
  });
  const scheduleOptions = useMemo(
    () => (availableQ.data ?? []).map((s) => ({ value: s.id, label: s.name ?? '(unnamed)' })),
    [availableQ.data],
  );

  const [feeScheduleId, setFeeScheduleId] = useState<UUID | null>(row.fee_schedule_id);
  const [freq, setFreq] = useState<string | null>(row.fee_frequency);
  const [vatPct, setVatPct] = useState<number | ''>(row.vat_rate_bps != null ? bpsToPct(row.vat_rate_bps) : '');
  const [name, setName] = useState(row.billing_name ?? '');
  const [tin, setTin] = useState(row.billing_tin ?? '');
  const [address, setAddress] = useState(row.billing_address ?? '');
  const [snapshotPersonId, setSnapshotPersonId] = useState<UUID | null>(null);
  const [snapshotName, setSnapshotName] = useState<string | null>(null);
  const [snapshotEdited, setSnapshotEdited] = useState(false);
  const [saving, setSaving] = useState(false);

  const draftBps = vatPct === '' ? null : pctToBps(vatPct);
  const assignmentChanged =
    feeScheduleId !== row.fee_schedule_id || draftBps !== row.vat_rate_bps || freq !== row.fee_frequency;
  const identityChanged =
    name !== (row.billing_name ?? '') || tin !== (row.billing_tin ?? '') || address !== (row.billing_address ?? '');
  const dirty = assignmentChanged || identityChanged || snapshotPersonId != null;

  const stageSnapshot = async (personId: UUID, label: string) => {
    try {
      const p = await personsApi.get(personId);
      const pv = previewFromPerson(p);
      setName(pv.name); setTin(pv.tin); setAddress(pv.address);
      setSnapshotPersonId(personId); setSnapshotName(label); setSnapshotEdited(false);
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof ApiError ? e.detail : 'Could not load the selected party.' });
    }
  };
  const editIdentity = (setter: (v: string) => void, v: string) => {
    setter(v);
    if (snapshotPersonId != null) setSnapshotEdited(true);
  };
  const clearSnapshot = () => {
    setSnapshotPersonId(null); setSnapshotName(null); setSnapshotEdited(false);
    setName(row.billing_name ?? ''); setTin(row.billing_tin ?? ''); setAddress(row.billing_address ?? '');
  };

  async function save() {
    if (!dirty) { onClose(); return; }
    setSaving(true);
    const identityVals = {
      billing_name: name.trim() || null,
      billing_tin: tin.trim() || null,
      billing_address: address.trim() || null,
    };
    try {
      // Identity: snapshot (server copies) then optional override edit; else a plain edit.
      if (snapshotPersonId) {
        await accountBillingApi.snapshot(agreementId, accountId, snapshotPersonId);
        if (snapshotEdited) await accountBillingApi.update(agreementId, accountId, identityVals);
      } else if (identityChanged) {
        await accountBillingApi.update(agreementId, accountId, identityVals);
      }
      // Assignment (independent axis, its own endpoint).
      if (assignmentChanged) {
        await accountBillingApi.setAssignment(agreementId, accountId, {
          fee_schedule_id: feeScheduleId,
          vat_rate_bps: draftBps,
          fee_frequency: freq,
        });
      }
      // Refresh the firm-wide list AND the per-agreement mirror surfaces (C-204/C-221).
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['account-billing', 'firm-wide'] }),
        qc.invalidateQueries({ queryKey: ['account-billing', agreementId] }),
        qc.invalidateQueries({ queryKey: ['account-fee-preview', agreementId] }),
        qc.invalidateQueries({ queryKey: ['basis-peers', agreementId, accountId] }),
      ]);
      notifications.show({ color: 'green', message: 'Billing details saved.' });
      onClose();
    } catch (e) {
      notifications.show({
        color: 'red', title: 'Could not save', autoClose: false,
        message: e instanceof ApiError ? e.detail : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack gap="sm" py="xs">
      {snapshotPersonId && (
        <Group gap="xs">
          <Badge color="orange" variant="light" leftSection={<IconUser size={12} />}>
            Snapshot from {snapshotName || 'party'}
          </Badge>
          <Tooltip label="Cancel snapshot — revert to the saved values" withArrow>
            <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Cancel snapshot" onClick={clearSnapshot}>
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
          <Text size="xs" c="dimmed">The server copies the party's current name / TIN / address on Save.</Text>
        </Group>
      )}

      <Group align="flex-end" gap="sm" wrap="wrap">
        <TextInput label="Billing name" value={name} w={220}
          onChange={(e) => editIdentity(setName, e.currentTarget.value)} />
        <TextInput label="Billing TIN" value={tin} w={160}
          onChange={(e) => editIdentity(setTin, e.currentTarget.value)} />
        <TextInput label="Billing address" value={address} w={300}
          onChange={(e) => editIdentity(setAddress, e.currentTarget.value)} />
        <div style={{ minWidth: 240 }}>
          <Text size="sm" fw={500} mb={4}>{snapshotPersonId ? 'Re-snapshot from a party' : 'Snapshot from a party'}</Text>
          <RelatedPartyPicker agreementId={agreementId} onPick={(pid, label) => { void stageSnapshot(pid, label); }} />
        </div>
      </Group>

      <Group align="flex-end" gap="sm" wrap="wrap">
        <Select label="Fee schedule" w={260}
          placeholder={availableQ.isLoading ? 'Loading…' : 'Unassigned'}
          data={scheduleOptions} value={feeScheduleId} onChange={setFeeScheduleId}
          clearable description="Only schedules available to this agreement." />
        <Select label="Billing frequency" w={160} placeholder="—" data={FREQUENCY_OPTIONS}
          value={freq} onChange={setFreq} clearable description="How often this account is billed." />
        <NumberInput label="VAT" w={120} min={0} max={100} step={0.01} decimalScale={2} suffix=" %"
          value={vatPct} onChange={(v) => setVatPct(typeof v === 'number' ? v : '')} />
      </Group>

      {assignmentChanged && (
        <Alert color="blue" variant="light" icon={<IconAlertTriangle size={16} />}>
          Changing a fee-schedule assignment recomputes the shared rate for the co-assigned
          accounts on that schedule — the exact fees are set at the next compute.
        </Alert>
      )}

      <Group gap="xs">
        <Button color="azul" loading={saving} onClick={save} disabled={!dirty}>Save changes</Button>
        <Button variant="default" onClick={onClose} disabled={saving}>Cancel</Button>
        {!dirty && <Text size="xs" c="dimmed">No changes yet.</Text>}
      </Group>
    </Stack>
  );
}

/** One-shot person picker scoped to the agreement's related parties (holders /
 *  representatives / beneficiaries). Reuses EntityPicker (open-on-click, C-183). */
function RelatedPartyPicker({
  agreementId, onPick,
}: { agreementId: string; onPick: (personId: UUID, name: string) => void }) {
  const partiesQ = useQuery({
    queryKey: ['agreement-party-persons', agreementId],
    queryFn: () => agreementPartiesApi.list({ agreement_id: agreementId, limit: 200 }),
  });
  const people = useMemo(() => {
    const seen = new Map<string, { id: UUID; display_name: string; role: string }>();
    for (const p of partiesQ.data?.items ?? []) {
      if (!p.person_id) continue;
      const ex = seen.get(p.person_id);
      if (ex) { if (!ex.role.includes(p.role)) ex.role = `${ex.role}, ${p.role}`; }
      else seen.set(p.person_id, { id: p.person_id, display_name: p.person_display_name ?? '(unnamed party)', role: p.role });
    }
    return [...seen.values()];
  }, [partiesQ.data]);
  return (
    <EntityPicker<{ id: UUID; display_name: string; role: string }>
      placeholder={people.length ? 'Pick a party…' : 'No parties on this agreement'}
      value={null}
      onChange={(id) => { if (!id) return; const m = people.find((x) => x.id === id); onPick(id, m?.display_name ?? ''); }}
      search={async (query) => {
        const ql = query.trim().toLowerCase();
        const items = ql ? people.filter((x) => x.display_name.toLowerCase().includes(ql)) : people;
        return { items, total: items.length, limit: 200, offset: 0 };
      }}
      getById={async (id) => people.find((x) => x.id === id) ?? { id, display_name: '', role: '' }}
      formatLabel={(r) => r.display_name}
      formatDetail={(r) => r.role || undefined}
      entityNoun="parties on this agreement"
    />
  );
}
